/**
 * tm/pages/MobileSched.tsx — 휴대폰 「상담 · 강의 일정」 보기 · 상담 등록 · 강의 등록 (2026-10-12 지도사 「상담, 강의 일정 조회 등록도 가능하게」)
 *  - 오늘부터 2주(한 달로 넓히기) · 날짜별 묶음 · 시간 칸 고정 폭(오와 열) · 상담은 누르면 그 업체 한눈에, 강의는 그 자리에서 펼침
 *  - 날짜 조율 중(후보 일정) 상담은 맨 아래 따로
 *  - 등록: PC 와 같은 저장 값(상담료 기본 10만원 개별 · WM 은 팀장님의 WM) · 겹치는 일정은 화면 안에서 알림(막지 않음 · 확인 창 없음)
 */
import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import * as api from '../api';
import { createLecture, emptyLecture } from '../api-lectures';
import type { Lecture, LectureDraft, LectureMode, RequesterKind } from '../api-lectures';
import type { Company, Consultation, FP } from '../types';
import { G, Icon, clamp } from '../portal-ui/tds';
import { addDays, d10, kstToday, md } from '../flow/flowLogic';
import { CONSULT_MIN, LECTURE_MIN, consultBusy, findConflicts, lectureBusy, toMin } from '../scheduleConflict';
import { Button, Card, Chips, DatePick, Field, NAVY, Next, Picker, SaveBar, Sheet, TOSS, Tint, guard, input } from './mobileKit';
import type { ByRecent } from './mobileKit';
import { fpLabel, fpOrg } from './MobileFind';

const MODE_T: Record<LectureMode, string> = { offline: '대면', online: '온라인', hybrid: '병행' };

export interface SchedItem { kind: 'consult' | 'lecture'; id: string; date: string; time: string; title: string; main: string; tail: string; sub: string; companyId?: string; lecture?: Lecture }

/** 확정 일정(날짜 있는 것) — 상담 · 강의 섞어 날짜 · 시간 순 */
export function schedItems(cons: Consultation[], lecs: Lecture[], from: string, to: string): SchedItem[] {
  const out: SchedItem[] = [];
  cons.forEach(c => {
    const d = d10(c.date);
    if (c.confirm_status === 'tentative' || !d || d < from || d > to) return;
    out.push({ kind: 'consult', id: c.id, date: d, time: String(c.time || '').slice(0, 5), companyId: c.company_id,
      title: `${c.company_name || '업체'}${c.fp_name ? ` · ${c.fp_name} 팀장` : ''}`, main: c.company_name || '업체', tail: c.fp_name ? `\u00a0· ${c.fp_name} 팀장` : '', sub: [c.topic, c.location].filter(Boolean).join(' · ') });
  });
  lecs.forEach(l => {
    const d = d10(l.lecture_date);
    if (l.status === 'canceled' || !d || d < from || d > to) return;
    out.push({ kind: 'lecture', id: l.id, date: d, time: String(l.start_time || '').slice(0, 5), lecture: l,
      title: l.title || '강의', main: l.title || '강의', tail: '',
      sub: (() => { const who = l.requester_org || l.requester_name; const at = l.place || (l.mode === 'online' ? '온라인' : ''); return (who && at.includes(who) ? [at] : [who, at]).filter(Boolean).join(' · '); })() });   // 장소에 소속이 들어 있으면 한 번만
  });
  return out.sort((a, b) => `${a.date} ${a.time || '99'}`.localeCompare(`${b.date} ${b.time || '99'}`));
}

/** 다음 일정 — 오늘 것은 시작 1시간 지난 것까지 · 시간 미정은 그날 맨 뒤 */
export function nextUp(items: SchedItem[], now: number = Date.now()): { item: SchedItem | null; today: number } {
  const t = kstToday(now);
  const kst = new Date(now + 9 * 3600 * 1000);
  const nowMin = kst.getUTCHours() * 60 + kst.getUTCMinutes();
  const todays = items.filter(i => i.date === t);
  const item = items.find(i => i.date > t || (i.date === t && (toMin(i.time) === null || (toMin(i.time) as number) >= nowMin - 60))) || null;
  return { item, today: todays.length };
}
export const whenText = (i: SchedItem) => `${i.date === kstToday() ? '오늘' : md(i.date)} ${i.time || '시간 미정'}`;

/* ── 일정 보기 ── */
export function SchedView({ consultations, lectures, onCompany, goConsult, goLecture }: {
  consultations: Consultation[]; lectures: Lecture[] | null; onCompany: (id: string) => void; goConsult: () => void; goLecture: () => void;
}) {
  const [days, setDays] = useState(14);
  const [open, setOpen] = useState<string | null>(null);
  const t = kstToday();
  const items = useMemo(() => schedItems(consultations, lectures || [], t, addDays(t, days - 1)), [consultations, lectures, t, days]);
  const tentative = consultations.filter(c => c.confirm_status === 'tentative' && c.status === 'scheduled');
  const byDate: { date: string; list: SchedItem[] }[] = [];
  items.forEach(i => { const g = byDate[byDate.length - 1]; if (g && g.date === i.date) g.list.push(i); else byDate.push({ date: i.date, list: [i] }); });
  const [sheet, setSheet] = useState(false);
  const seg = (k: number, label: string) => (
    <button key={k} type="button" className="tds-btn" onClick={() => setDays(k)} aria-pressed={days === k}
      style={{ height: 40, borderRadius: 10, border: 'none', background: days === k ? '#fff' : 'transparent', boxShadow: days === k ? '0 1px 3px rgba(0,0,0,.12)' : 'none', color: days === k ? TOSS.text : TOSS.sub, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{label}</button>
  );
  const rowBtn: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 72, padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' };
  return (
    <div style={{ paddingTop: 4 }}>
      {/* 기간 — 회색 통 안 흰 알약(토스식 선택 막대) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, padding: 4, borderRadius: 14, background: TOSS.line, marginBottom: 12 }}>{seg(14, '2주')}{seg(31, '한 달')}</div>
      {lectures === null && <div style={{ padding: '12px 4px', fontSize: 16, color: TOSS.sub }}>강의 일정 불러오는 중</div>}
      {byDate.length === 0 && lectures !== null && <Card pad="20px"><div style={{ fontSize: 16, color: TOSS.sub }}>{days === 14 ? '2주' : '한 달'} 안 일정 없음</div></Card>}
      {byDate.map(g => (
        <div key={g.date}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '10px 4px 8px' }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: g.date === t ? TOSS.blue : TOSS.text }}>{md(g.date)}</span>
            {g.date === t && <span style={{ fontSize: 15, fontWeight: 700, color: TOSS.blue }}>오늘</span>}
            {g.date === addDays(t, 1) && <span style={{ fontSize: 15, fontWeight: 700, color: TOSS.sub }}>내일</span>}
          </div>
          <Card pad="4px 0">
          {g.list.map(i => {
            const on = open === i.kind + i.id;
            const l = i.lecture;
            return (
              <div key={i.kind + i.id}>
                <button type="button" className="tds-row" onClick={() => (i.kind === 'consult' && i.companyId ? onCompany(i.companyId) : setOpen(on ? null : i.kind + i.id))} aria-expanded={i.kind === 'lecture' ? on : undefined} style={rowBtn}>
                  <span style={{ width: 52, flexShrink: 0, fontSize: 17, fontWeight: 700, color: TOSS.text, fontVariantNumeric: 'tabular-nums' }}>{i.time || '미정'}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {/* 업체 이름은 한 줄을 다 쓰고, 팀장님은 둘째 줄 앞에(「대한…」처럼 잘리지 않게) */}
                    <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: TOSS.text, ...clamp(1) }}>{i.main}</span>
                    {/* 「상담」 칩 칸을 없애 업체 이름 자리를 넓힘 — 강의만 둘째 줄 앞에 「강의」 */}
                    <span style={{ display: 'block', fontSize: 15, color: TOSS.sub, ...clamp(1) }}>{[i.kind === 'lecture' ? '강의' : '', i.tail.replace(/^\u00a0· /, ''), i.sub].filter(Boolean).join(' · ') || ' '}</span>
                  </span>
                  <Icon name={i.kind === 'lecture' && on ? 'd' : 'r'} size={18} color={G.g400} sw={2} />
                </button>
                {on && l && (
                  <div style={{ padding: '0 20px 14px 84px', fontSize: 16, color: TOSS.sub, lineHeight: 1.7 }}>
                    <div>{[l.start_time && l.end_time ? `${l.start_time.slice(0, 5)}~${l.end_time.slice(0, 5)}` : '', MODE_T[l.mode] || '', l.place].filter(Boolean).join(' · ')}</div>
                    {(l.requester_name || l.requester_org) && <div>요청 {[l.requester_name, l.requester_org].filter(Boolean).join(' · ')}</div>}
                    {(l.headcount || l.fee_manwon) && <div>{[l.headcount ? `${l.headcount}명` : '', l.fee_manwon ? `강의료 ${l.fee_manwon.toLocaleString('ko-KR')}만원` : ''].filter(Boolean).join(' · ')}</div>}
                    <div>{l.status === 'confirmed' ? '확정' : l.status === 'done' ? '마침' : '예정'}</div>
                    {l.memo && <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{l.memo}</div>}
                  </div>
                )}
              </div>
            );
          })}
          </Card>
        </div>
      ))}
      {tentative.length > 0 && (
        <Card pad="4px 0">
          <div style={{ padding: '10px 20px 2px', fontSize: 17, fontWeight: 700, color: TOSS.text }}>날짜 조율 중 <span style={{ color: TOSS.faint }}>{tentative.length}</span></div>
          {tentative.map(c => (
            <button key={c.id} type="button" className="tds-row" onClick={() => onCompany(c.company_id)} style={rowBtn}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: TOSS.text, ...clamp(1) }}>{c.company_name || '업체'}</span>
                <span style={{ display: 'block', fontSize: 15, color: TOSS.sub, fontVariantNumeric: 'tabular-nums', ...clamp(1) }}>{c.fp_name ? `${c.fp_name} 팀장 · ` : ''}후보 {(c.candidate_slots || []).filter(x => x.date).map(x => `${md(x.date)} ${x.time || ''}`.trim()).join(' · ') || '없음'}</span>
              </span>
              <Icon name="r" size={18} color={G.g400} sw={2} />
            </button>
          ))}
        </Card>
      )}
      {/* 주된 행동은 아래 하나(토스식) — 누르면 상담 · 강의 고르기 */}
      <SaveBar side={16}><Button onClick={() => setSheet(true)}>일정 등록하기</Button></SaveBar>
      <Sheet open={sheet} onClose={() => setSheet(false)} title="무엇을 등록할까요">
        {[{ k: 'c', icon: 'brief', t: '상담 등록', on: goConsult }, { k: 'l', icon: 'mic', t: '강의 등록', on: goLecture }].map(x => (
          <button key={x.k} type="button" className="tds-row" onClick={() => { setSheet(false); x.on(); }}
            style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', minHeight: 64, padding: '8px 0', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', fontSize: 18, fontWeight: 700, color: TOSS.text }}>
            <Tint icon={x.icon} />{x.t}
          </button>
        ))}
      </Sheet>
    </div>
  );
}

/* ── 겹침 안내(화면 안 · 막지 않음) ── */
function Clash({ consultations, lectures, date, start, end, min }: { consultations: Consultation[]; lectures: Lecture[]; date: string; start: string; end: string; min: number }) {
  const r = useMemo(() => findConflicts([...consultBusy(consultations), ...lectureBusy(lectures)], date || null, start, end, min), [consultations, lectures, date, start, end, min]);
  if (!date || (!r.overlaps.length && !r.untimed.length)) return null;
  const list = r.overlaps.length ? r.overlaps : r.untimed;
  return (
    <div role="status" style={{ marginTop: 14, padding: '12px 14px', borderRadius: 14, background: '#FFF7ED', border: '1px solid #FED7AA', color: '#9A3412', fontSize: 15, lineHeight: 1.6 }}>
      <div style={{ fontWeight: 800 }}>{r.overlaps.length ? '시간이 겹치는 일정이 있어요' : '같은 날 일정이 있어요'}</div>
      {list.slice(0, 4).map(b => <div key={b.kind + b.id + b.time} style={clamp(1)}>{b.kind === 'lecture' ? '강의' : '상담'} {b.time} {b.label}</div>)}
      <div style={{ color: '#7C2D12', fontSize: 14 }}>그래도 저장할 수 있어요</div>
    </div>
  );
}

const timeIn: CSSProperties = { ...input, fontVariantNumeric: 'tabular-nums' };
function TimeIn({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return <input className="tds-in" type="time" value={value} onChange={e => onChange(e.target.value)} aria-label={label} step={600} style={timeIn} />;
}
/** 자주 잡는 시간 칩 + 다른 시간 — 시계 돌리지 않고 누름 한 번(패널) */
function TimePick({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const opts = ['10:00', '11:00', '14:00', '15:00', '16:00'].map(k => ({ k, t: `${+k.slice(0, 2)}시` }));
  return (
    <div>
      <Chips cols={5} items={opts} value={[value]} onChange={v => onChange(v[0] === value ? '' : v[0])} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <span style={{ flexShrink: 0, fontSize: 15, color: TOSS.sub }}>다른 시간</span>
        <div style={{ flex: 1, minWidth: 0 }}><TimeIn value={value} onChange={onChange} label={label} /></div>
      </div>
    </div>
  );
}
const area: CSSProperties = { ...input, height: 'auto', minHeight: 96, padding: '12px 14px', lineHeight: 1.5, resize: 'none' };

/* ── 상담 등록 ── */
export function ConsultNewForm({ preCompany, preFp, companies, fps, consultations, lectures, compSub, byRecent, toast, onSaved, onCompany, goSched }: {
  preCompany?: string; preFp?: string; companies: Company[]; fps: FP[]; consultations: Consultation[]; lectures: Lecture[];
  compSub: (c: Company) => string; byRecent: ByRecent; toast: (m: string) => void; onSaved: () => Promise<void>; onCompany: (id: string) => void; goSched: () => void;
}) {
  const firstFp = (cid: string | null) => { const c = companies.find(x => x.id === cid); const f = c && (c.fps || [])[0]; return f ? f.fp_id : null; };
  const [cid, setCid] = useState<string | null>(preCompany || null);
  const [fpId, setFpId] = useState<string | null>(preFp || firstFp(preCompany || null));
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [loc, setLoc] = useState('');
  const [topic, setTopic] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const comp = companies.find(c => c.id === cid);
  const fp = fps.find(f => f.id === fpId);
  // 팀장님을 먼저 고르면 그 팀장님 업체가 「최근」 맨 위로
  const mineIds = useMemo(() => new Set(fpId ? companies.filter(c => (c.fps || []).some(x => x.fp_id === fpId)).map(c => c.id) : []), [companies, fpId]);
  const isMine = (x: object) => mineIds.has(String((x as { id?: string }).id || ''));
  const recentFirst: ByRecent = (a, b) => Number(isMine(b)) - Number(isMine(a)) || byRecent(a, b);
  const missing = !cid ? '업체를 고르세요' : !fpId ? '팀장님을 고르세요' : !date ? '날짜를 고르세요' : '';
  // 업체를 고르면 그 업체 팀장님으로(지금 고른 팀장님이 그 업체 팀장님이면 그대로)
  const pickCo = (id: string | null) => {
    setCid(id);
    if (!id) return;
    const c = companies.find(x => x.id === id);
    const linked = !!fpId && !!c && (c.fps || []).some(x => x.fp_id === fpId);
    const f = firstFp(id);
    if (f && !linked) setFpId(f);
  };
  const [more, setMore] = useState(false);   // 장소 · 주제 · 메모는 접어 둠(폼 길이 · 검토)
  const save = async () => {
    if (!guard(missing, busy, toast) || !cid) return;
    setBusy(true);
    try {
      const r = await api.createConsultation({
        company_id: cid, fp_id: fpId, date, time, location: loc.trim(), topic: topic.trim(), notes: notes.trim(),
        confirm_status: 'confirmed', candidate_slots: [], fee_type: 'paid', fee_method: 'individual', fee_amount: 100000, fee_memo: '',
        wm_id: fp ? fp.wm_id : null,
      });
      if (!r.success) throw new Error(r.error || '저장하지 못했어요');
      await onSaved();
      toast(`${md(date)} ${time || ''} 상담을 등록했어요`.replace(/\s+/g, ' '));
      setSaved(cid); setDate(''); setTime(''); setTopic(''); setNotes('');
    } catch (e: unknown) { toast(e instanceof Error ? e.message : '저장하지 못했어요'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Field title="업체" need><Picker items={companies} value={cid} onChange={pickCo} sub={compSub} placeholder="업체 이름으로 찾기" recent={recentFirst} /></Field>
      <Field title="팀장님" need note={fp && fp.wm_name ? `WM ${fp.wm_name} 으로 저장해요` : undefined}>
        <Picker items={fps.map(f => ({ ...f, name: fpLabel(f) }))} value={fpId} onChange={setFpId} sub={f => fpOrg(f)} placeholder="팀장님 이름으로 찾기" recent={byRecent} />
      </Field>
      <Field title="날짜" need><DatePick value={date} onChange={setDate} /></Field>
      <Field title="시간" note="비워 두면 「시간 미정」"><TimePick value={time} onChange={setTime} label="상담 시간" /></Field>
      <Clash consultations={consultations} lectures={lectures} date={date} start={time} end="" min={CONSULT_MIN} />
      {!more && !loc && !topic && !notes ? (
        <button type="button" className="tds-btn" onClick={() => setMore(true)} style={{ marginTop: 20, width: '100%', height: 52, borderRadius: 14, border: 'none', background: TOSS.bg, color: TOSS.sub, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>장소 · 주제 · 메모 더 적기</button>
      ) : (<>
      <Field title="장소">
        <input className="tds-in" value={loc} onChange={e => setLoc(e.target.value)} maxLength={100} placeholder="예) 업체 사무실" aria-label="장소" style={input} />
        {comp && comp.address && !loc && <button type="button" className="tds-btn" onClick={() => setLoc(comp.address)} style={{ marginTop: 6, minHeight: 44, padding: '0 2px', border: 'none', background: 'none', color: NAVY, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', maxWidth: '100%', textAlign: 'left', ...clamp(1) }}>업체 주소 넣기 · {comp.address}</button>}
      </Field>
      <Field title="주제"><input className="tds-in" value={topic} onChange={e => setTopic(e.target.value)} maxLength={100} placeholder="예) 정책자금 · 가업승계" aria-label="주제" style={input} /></Field>
      <Field title="메모"><textarea className="tds-in" value={notes} onChange={e => setNotes(e.target.value)} rows={3} aria-label="메모" style={area} /></Field>
      </>)}
      <div style={{ marginTop: 14, fontSize: 15, color: TOSS.sub }}>상담료 10만원 · 개별 정산(바꾸려면 PC)</div>
      {saved && <Next items={[{ t: '이 업체 보기', on: () => onCompany(saved) }, { t: '일정 보기', on: goSched }]} />}
      <SaveBar>
        <button type="button" className="tds-btn" onClick={() => void save()} aria-disabled={!!missing || busy}
          style={{ width: '100%', height: 56, borderRadius: 16, border: 'none', background: missing ? TOSS.line : TOSS.blue, color: missing ? TOSS.sub : '#fff', opacity: busy ? 0.6 : 1, fontSize: 18, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{busy ? '저장 중' : missing || '상담 등록'}</button>
      </SaveBar>
    </>
  );
}

/* ── 강의 등록 ── */
export function LectureNewForm({ fps, wms, consultations, lectures, byRecent, toast, onSaved, goSched }: {
  fps: FP[]; wms: { id: string; name: string; company: string; phone?: string }[]; consultations: Consultation[]; lectures: Lecture[];
  byRecent: ByRecent; toast: (m: string) => void; onSaved: () => Promise<void>; goSched: () => void;
}) {
  const [f, setF] = useState<LectureDraft>(() => ({ ...emptyLecture(), requester_kind: 'fp' }));
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = <K extends keyof LectureDraft>(k: K, v: LectureDraft[K]) => setF(x => ({ ...x, [k]: v }));
  const date = f.lecture_date || '';
  const missing = !f.title.trim() ? '강의 주제를 적어 주세요' : !date ? '날짜를 고르세요' : '';
  const pickFp = (id: string | null) => { const p = fps.find(x => x.id === id); setF(x => ({ ...x, requester_ref_id: id, requester_name: p ? fpLabel(p) : '', requester_org: p ? fpOrg(p) : '', requester_phone: p ? p.phone : '' })); };
  const pickWm = (id: string | null) => { const w = wms.find(x => x.id === id); setF(x => ({ ...x, requester_ref_id: id, requester_name: w ? w.name : '', requester_org: w ? w.company : '', requester_phone: w && w.phone ? w.phone : '' })); };
  const kind = (k: RequesterKind) => setF(x => ({ ...x, requester_kind: k, requester_ref_id: null, requester_name: '', requester_org: '', requester_phone: '' }));
  const numOf = (v: string) => { const n = parseInt(v.replace(/\D/g, ''), 10); return isNaN(n) ? null : Math.min(n, 99999); };
  const save = async () => {
    if (!guard(missing, busy, toast)) return;
    setBusy(true);
    try {
      const r = await createLecture({ ...f, title: f.title.trim(), place: f.place.trim(), memo: f.memo.trim() });
      if (!r.success) throw new Error(r.error || '저장하지 못했어요');
      await onSaved();
      toast(`${md(date)} 강의를 등록했어요`);
      setSaved(true);
      setF(x => ({ ...emptyLecture(), requester_kind: x.requester_kind }));
    } catch (e: unknown) { toast(e instanceof Error ? e.message : '저장하지 못했어요'); }
    finally { setBusy(false); }
  };
  const half: CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 8 };
  return (
    <>
      <Field title="강의 주제" need><input className="tds-in" value={f.title} onChange={e => set('title', e.target.value)} maxLength={120} placeholder="예) 벤처투자 소득공제" aria-label="강의 주제" style={input} /></Field>
      <Field title="날짜" need><DatePick value={date} onChange={v => set('lecture_date', v)} /></Field>
      <Field title="시작 · 끝">
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 16px minmax(0,1fr)', gap: 6, alignItems: 'center' }}>
          <TimeIn value={f.start_time} onChange={v => set('start_time', v)} label="시작 시간" /><span style={{ textAlign: 'center', color: TOSS.sub, fontSize: 17 }}>~</span><TimeIn value={f.end_time} onChange={v => set('end_time', v)} label="끝 시간" />
        </div>
      </Field>
      <Clash consultations={consultations} lectures={lectures} date={date} start={f.start_time} end={f.end_time} min={LECTURE_MIN} />
      <Field title="방식"><Chips cols={3} items={[{ k: 'offline', t: '대면' }, { k: 'online', t: '온라인' }, { k: 'hybrid', t: '병행' }]} value={[f.mode]} onChange={v => set('mode', v[0] as LectureMode)} /></Field>
      <Field title={f.mode === 'online' ? '접속 링크' : '장소'}><input className="tds-in" value={f.place} onChange={e => set('place', e.target.value)} maxLength={200} placeholder={f.mode === 'online' ? 'Zoom 링크' : '예) 한화생명 강남지점 교육장'} aria-label="장소" style={input} /></Field>
      <Field title="요청한 곳">
        <Chips cols={3} items={[{ k: 'fp', t: '팀장님' }, { k: 'wm', t: 'WM' }, { k: 'etc', t: '기타' }]} value={[f.requester_kind]} onChange={v => kind(v[0] as RequesterKind)} />
        <div style={{ marginTop: 8 }}>
          {f.requester_kind === 'fp' && <Picker items={fps.map(p => ({ ...p, name: fpLabel(p) }))} value={f.requester_ref_id} onChange={pickFp} sub={p => fpOrg(p)} placeholder="팀장님 이름으로 찾기" recent={byRecent} optional="팀장님 고르기(선택)" />}
          {f.requester_kind === 'wm' && <Picker items={wms} value={f.requester_ref_id} onChange={pickWm} sub={w => w.company} placeholder="WM 이름으로 찾기" recent={byRecent} optional="WM 고르기(선택)" />}
          {f.requester_kind === 'etc' && (
            <div style={half}>
              <input className="tds-in" value={f.requester_name} onChange={e => set('requester_name', e.target.value)} maxLength={40} placeholder="이름" aria-label="요청한 분 이름" style={input} />
              <input className="tds-in" value={f.requester_org} onChange={e => set('requester_org', e.target.value)} maxLength={60} placeholder="소속" aria-label="요청한 곳 소속" style={input} />
            </div>
          )}
        </div>
      </Field>
      {!more ? (
        <button type="button" className="tds-btn" onClick={() => setMore(true)} style={{ marginTop: 20, width: '100%', height: 52, borderRadius: 14, border: 'none', background: TOSS.bg, color: TOSS.sub, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>인원 · 강의료 · 확정 · 메모 더 적기</button>
      ) : (<>
      <Field title="인원 · 강의료">
        <div style={{ ...half, fontSize: 14, color: TOSS.sub, marginBottom: 4 }}><span>인원</span><span>강의료(만원)</span></div>
        <div style={half}>
          <input className="tds-in" inputMode="numeric" value={f.headcount == null ? '' : String(f.headcount)} onChange={e => set('headcount', numOf(e.target.value))} placeholder="명" aria-label="인원" style={{ ...input, textAlign: 'right' }} />
          <input className="tds-in" inputMode="numeric" value={f.fee_manwon == null ? '' : String(f.fee_manwon)} onChange={e => set('fee_manwon', numOf(e.target.value))} placeholder="만원" aria-label="강의료 만원" style={{ ...input, textAlign: 'right' }} />
        </div>
      </Field>
      <Field title="상태"><Chips cols={2} items={[{ k: 'planned', t: '예정' }, { k: 'confirmed', t: '확정' }]} value={[f.status]} onChange={v => set('status', v[0] as LectureDraft['status'])} /></Field>
      <Field title="메모"><textarea className="tds-in" value={f.memo} onChange={e => set('memo', e.target.value)} rows={3} aria-label="메모" style={area} /></Field>
      </>)}
      {saved && <Next items={[{ t: '일정 보기', on: goSched }]} />}
      <SaveBar>
        <button type="button" className="tds-btn" onClick={() => void save()} aria-disabled={!!missing || busy}
          style={{ width: '100%', height: 56, borderRadius: 16, border: 'none', background: missing ? TOSS.line : TOSS.blue, color: missing ? TOSS.sub : '#fff', opacity: busy ? 0.6 : 1, fontSize: 18, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{busy ? '저장 중' : missing || '강의 등록'}</button>
      </SaveBar>
    </>
  );
}
